import DOMPurify from 'isomorphic-dompurify'

// Non-breaking space character - makes empty paragraphs non-empty so they're preserved
// but visually appears as a line break
const NBSP = '\u00A0'

export function sanitizeHtmlContent(content?: string | null): string | null {
  if (typeof content !== 'string') {
    return null
  }

  // Preserve empty paragraphs by adding a non-breaking space
  // TipTap normalizes truly empty <p></p> tags, but <p>&nbsp;</p> is preserved
  // The non-breaking space is invisible but keeps the paragraph structure
  let processedContent = content
  
  // Replace empty paragraphs with paragraphs containing a non-breaking space
  // Match <p></p>, <p> </p> (whitespace only), or <p><br></p>
  // This preserves the paragraph structure while maintaining line breaks
  processedContent = processedContent.replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, `<p>${NBSP}</p>`)
  
  // Sanitize the content with DOMPurify
  const sanitized = DOMPurify.sanitize(processedContent, { 
    // Explicitly allow all tags that TipTap uses
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'strike', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'a', 'span', 'div', 'code', 'pre'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'id'],
  })
  
  // After sanitization, check if there's any meaningful content
  // Remove all HTML tags and check if there's any text content
  // Note: We need to preserve non-breaking spaces as they represent line breaks
  const textContent = sanitized.replace(/<[^>]*>/g, '').replace(/\u00A0/g, '').trim()
  
  // Return null only if there's no text content AND no HTML structure
  // Preserve HTML structure even if it's just paragraphs with non-breaking spaces (which represent line breaks)
  if (textContent.length === 0) {
    // Check if there's any HTML structure (tags) - if so, preserve it
    // This includes <p>&nbsp;</p> tags that represent intentional line breaks
    const hasHtmlStructure = /<[^>]+>/.test(sanitized)
    if (!hasHtmlStructure) {
      return null
    }
    // If there's HTML structure but no text, return the sanitized HTML (preserves line breaks)
    return sanitized
  }

  return sanitized
}
